/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { Typography, Divider, Chip, Stack, Grid, Box, CardActions, Card, CardContent, Collapse } from "@mui/material";

import React, { useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FullAssignedTechnicianEntry } from "../../utils/Interfaces";
import ExpandMore from "../../components/general/ExpandMore";
import { MaterialsTable } from "../../components/table/MaterialsTable";

export interface TechnicianWorkOrderHistoryProps {
    history: FullAssignedTechnicianEntry[];
}

interface RowProps {
    label: string;
    value: any;
}

interface ChecklistRowProps {
    label: string;
    value: any;
    reverse: boolean;
}

const TechnicianWorkOrderHistory = (props: TechnicianWorkOrderHistoryProps) => {
    const [expanded, setExpanded] = useState<boolean>(false);

    const { history } = props;

    return (
        <Card sx={{ minWidth: 275, mt: 1, pb: 0 }}>
            {/* There is a global MUI CSS style that sets a padding of 24px, override it in this specific area */}
            <CardContent sx={{ pr: 0, paddingBottom: "16px !important" }}>
                <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" justifyContent="space-between">
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                            }}
                        >
                            <Typography sx={{ fontSize: 25 }} color="text.primary">
                                {`View ${history.length} Previous Trip${history.length !== 1 ? "s" : ""}`}
                            </Typography>
                        </Box>
                    </Stack>
                    <CardActions sx={{ pt: 0, pb: 0 }}>
                        {/* @ts-ignore */}
                        <ExpandMore
                            expand={expanded}
                            onClick={() => setExpanded(!expanded)}
                            aria-expanded={expanded}
                            aria-label="show more"
                        >
                            <ExpandMoreIcon />
                        </ExpandMore>
                    </CardActions>
                </Stack>
            </CardContent>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent>
                    {/* TODO Make a new component for this. */}
                    {history.map((trip: FullAssignedTechnicianEntry, index) => {
                        const Row = (props: RowProps) => {
                            const { label, value } = props;
                            return (
                                <Grid container display="flex" flexDirection="row" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ marginTop: "5px" }}>
                                        <b>{label}</b>
                                    </Typography>
                                    <Typography variant="overline">{value}</Typography>
                                </Grid>
                            );
                        };

                        const ChecklistRow = (props: ChecklistRowProps) => {
                            const { label, value, reverse } = props;

                            return (
                                <Row
                                    label={label}
                                    value={
                                        value === null ? (
                                            <Chip color="default" label="N/A" style={{ fontWeight: "bold" }} />
                                        ) : value ? (
                                            <Chip
                                                color={reverse ? "error" : "success"}
                                                label="yes"
                                                style={{ fontWeight: "bold" }}
                                            />
                                        ) : (
                                            <Chip
                                                color={reverse ? "success" : "error"}
                                                label="No"
                                                style={{ fontWeight: "bold" }}
                                            />
                                        )
                                    }
                                />
                            );
                        };

                        return (
                            <Box key={index}>
                                <Divider>
                                    <Typography variant={"h5"}>
                                        <b>{`Trip #${trip.trip_number}`}</b>
                                    </Typography>
                                </Divider>

                                <Box sx={{ mb: 2 }} />

                                <Row
                                    label="Travel Start Time"
                                    value={trip.travel_start_time !== null ? trip.travel_start_time : "--"}
                                />
                                <Row
                                    label="Time Arrived on Site"
                                    value={trip.time_arrived_on_site !== null ? trip.time_arrived_on_site : "--"}
                                />
                                <Row label="Time Left Site" value={trip.time_site_left !== null ? trip.time_site_left : "--"} />

                                <Box sx={{ mb: 3 }} />

                                <Typography variant="body2" fontWeight="bold">
                                    Work Performed
                                </Typography>
                                <Typography variant="overline">
                                    {trip.work_performed !== null && trip.work_performed !== "" ? trip.work_performed : "--"}
                                </Typography>
                                <Box sx={{ mb: 2 }} />

                                <ChecklistRow label="Return Trip Needed" value={trip.return_trip_needed} reverse />
                                <Box sx={{ mb: 2 }} />

                                <Typography variant="body2" fontWeight="bold">
                                    Parts Needed to Return
                                </Typography>
                                <Typography variant="overline">
                                    {trip.parts_needed_to_return !== null && trip.parts_needed_to_return !== ""
                                        ? trip.parts_needed_to_return
                                        : "--"}
                                </Typography>
                                <Box sx={{ mb: 3 }} />

                                <Typography variant="body2" fontWeight="bold">
                                    Other Notes
                                </Typography>
                                <Typography variant="overline">
                                    {trip.other_notes !== null && trip.other_notes !== "" ? trip.other_notes : "--"}
                                </Typography>
                                <Box sx={{ mb: 2 }} />

                                {trip.materials.length > 0 && (
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            Parts/Materials Used
                                        </Typography>
                                        <MaterialsTable
                                            technicianEntry={trip}
                                            editing={false}
                                            showMaterialPrice={false}
                                            addMaterial={() => {}}
                                            updateMaterial={() => {}}
                                        />
                                    </Box>
                                )}

                                <Box sx={{ mb: 3 }} />

                                {trip.tasks.map((task) => {
                                    return (
                                        <>
                                            <ChecklistRow label={task.label} value={task.status} reverse={false} />
                                            <Box sx={{ mb: 2 }} />
                                        </>
                                    );
                                })}

                                <Box sx={{ mb: 3 }} />
                                <Row label={"Signature"} value={trip.time_site_left !== null ? trip.signature : "--"} />
                                {index < history.length - 1 && <Box sx={{ mb: 3 }} />}
                            </Box>
                        );
                    })}
                </CardContent>
            </Collapse>
        </Card>
    );
};

export default TechnicianWorkOrderHistory;
